import {
  UILeg,
  UIFlightSegment,
  UIFlightSegmentTimelined,
  UITimelineLeg,
  UITimelineStop,
  UITimelineStopType,
  UIAirlineReferenceByLeg
} from "@flights/types";
import { formattingUtils } from "@flights/web-api-utils-universal";

export default function makeTimelineFromSegment(
  segment: UIFlightSegment,
  segmentIndex: number,
  airlineReferencesByLeg?: UIAirlineReferenceByLeg[]
): UIFlightSegmentTimelined {
  const {
    departureTime,
    arrivalTime,
    departureAirport,
    arrivalAirport,
    travellerCabinLuggage,
    travellerCheckedLuggage,
    legs,
    totalTime,
    isAtolProtected,
    isVirtualInterlining
  } = segment;

  if (
    !legs ||
    !departureAirport ||
    !arrivalAirport ||
    !travellerCabinLuggage ||
    !travellerCheckedLuggage ||
    !departureTime ||
    !totalTime ||
    !arrivalTime
  ) {
    throw new Error("timelineFromSegment: missing segment data");
  }
  const durationMinutes = totalTime / 60;

  const stops = legs.length - 1;
  const timeline: UIFlightSegmentTimelined["timeline"] = [];
  legs.forEach((leg, index: number) => {
    if (
      !leg.departureAirport ||
      !leg.arrivalAirport ||
      !leg.flightInfo ||
      !leg.departureTime ||
      !leg.arrivalTime ||
      !leg.totalTime ||
      !leg.flightInfo.carrierInfo
    ) {
      throw new Error("timelineFromSegment: missing leg data");
    }
    if (legs && legs.length > 1 && index !== 0) {
      const previousLeg = legs[index - 1];
      if (!previousLeg || !previousLeg.arrivalTime) {
        throw new Error("timelineFromSegment: could not get previous leg for connection info");
      }
      timeline.push(makeStopRow(leg, previousLeg));
    }

    /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
    // FIXME: carries was deprecated, use flightInfo (we should add carries data to flightInfo)
    const carrier = leg.carriersData[0];
    const operatingCarrier =
      leg.flightInfo.carrierInfo &&
      leg.flightInfo.carrierInfo.operatingCarrier !== leg.flightInfo.carrierInfo.marketingCarrier
        ? leg.carriersData[1]
        : undefined;
    const operatingCarrierDisclosureText = leg.flightInfo.carrierInfo.operatingCarrierDisclosureText;

    const flightRow: UITimelineLeg = {
      type: "FLIGHT",
      from: {
        airport: leg.departureAirport.name,
        airportCode: leg.departureAirport.code,
        city: leg.departureAirport.cityName || "",
        country: leg.departureAirport.countryName || "",
        departureTime: leg.departureTime
      },
      to: {
        airport: leg.arrivalAirport.name,
        airportCode: leg.arrivalAirport.code,
        city: leg.arrivalAirport.cityName || "",
        country: leg.arrivalAirport.countryName || "",
        arrivalTime: leg.arrivalTime
      },
      carrier: carrier ? carrier.name : "-",
      carrierCode: carrier ? carrier.code : "-",
      carrierLogo: carrier ? carrier.logo : "",
      flightDurationMinutes: Math.floor(leg.totalTime / 60),
      flightNumber: leg.flightInfo.flightNumber + "",
      cabinClass: leg.cabinClass,
      vi: leg.vi,
      flightStops: leg.flightStops,
      amenities: leg.amenities || []
    };
    if (operatingCarrier) {
      flightRow.operatingCarrier = operatingCarrier.name;
    }
    if (operatingCarrierDisclosureText) {
      flightRow.operatingCarrierDisclosureText = operatingCarrierDisclosureText;
    }
    if (airlineReferencesByLeg) {
      const airlineReference = airlineReferencesByLeg.find(
        (airlineReferenceByLeg) =>
          airlineReferenceByLeg.legIdentifier.findIndex(
            (legIndetifier) => legIndetifier.segmentIndex === segmentIndex && legIndetifier.legIndex === index
          ) !== -1
      );
      if (airlineReference) {
        flightRow.airlineReference = airlineReference.reference;
      }
    }
    timeline.push(flightRow);
  });

  return {
    departureAirport,
    departureTime,
    arrivalAirport,
    arrivalTime,
    travellerCabinLuggage,
    travellerCheckedLuggage,
    legs,
    totalTime,
    durationMinutes,
    stops,
    timeline,
    isAtolProtected,
    isVirtualInterlining
  };
}

const makeStopRow = (currentLeg: UILeg, previousLeg: UILeg): UITimelineStop => {
  let stopType: UITimelineStopType;
  if (previousLeg.flightStops.some((stop) => stop.changeOfGauge)) {
    stopType = "CHANGE_OF_GAUGE";
  } else if (currentLeg.departureAirport.code !== previousLeg.arrivalAirport.code) {
    stopType = "CHANGE_OF_AIRPORTS";
  } else {
    stopType = "AIRPORT";
  }

  return {
    type: "STOP",
    layoverType: stopType,
    airport: currentLeg.departureAirport,
    startTime: previousLeg.arrivalTime || "",
    endTime: currentLeg.departureTime || "",
    durationMinutes: formattingUtils.durationInMinutes(previousLeg.arrivalTime, currentLeg.departureTime)
  };
};
